import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import RefreshIcon from "@material-ui/icons/Refresh";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  title: {
    fontWeight: 700,
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  select: {
    minWidth: 180,
  },
  helper: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: theme.spacing(1),
  },
  warning: {
    color: theme.palette.warning.dark,
    fontSize: "0.8rem",
    marginTop: theme.spacing(1),
  },
  chip: {
    height: 22,
    fontSize: "0.75rem",
  },
}));

function valueOrEmpty(value) {
  return value || "";
}

export default function GuardReleasePolicyCard({
  releases = [],
  policy,
  envOverrides = {},
  loading,
  onRefresh,
  onSave,
}) {
  const classes = useStyles();
  const [latestVersion, setLatestVersion] = useState("");
  const [minimumVersion, setMinimumVersion] = useState("");

  useEffect(() => {
    setLatestVersion(valueOrEmpty(policy?.latestVersion));
    setMinimumVersion(valueOrEmpty(policy?.minimumVersion));
  }, [policy?.latestVersion, policy?.minimumVersion]);

  const envOverrideActive = Boolean(envOverrides?.latestVersion || envOverrides?.minimumVersion);
  const hasReleases = releases.length > 0;
  const saveDisabled = loading || !hasReleases || (!latestVersion && minimumVersion);

  return (
    <Paper className={classes.card}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>Release Policy</Typography>
          <Typography className={classes.helper}>
            Select the latest and minimum Ouro Guard versions from S3 release artifacts.
          </Typography>
        </div>
        {policy?.updatedAt && (
          <Chip
            className={classes.chip}
            variant="outlined"
            size="small"
            label={`Updated ${new Date(policy.updatedAt).toLocaleString()}`}
          />
        )}
      </div>

      <div className={classes.controls}>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel id="guard-latest-version-label">Latest available</InputLabel>
          <Select
            labelId="guard-latest-version-label"
            value={latestVersion}
            onChange={(event) => setLatestVersion(event.target.value)}
            label="Latest available"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {releases.map((release) => (
              <MenuItem key={release.version} value={release.version}>
                {release.version}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel id="guard-minimum-version-label">Minimum required</InputLabel>
          <Select
            labelId="guard-minimum-version-label"
            value={minimumVersion}
            onChange={(event) => setMinimumVersion(event.target.value)}
            label="Minimum required"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {releases.map((release) => (
              <MenuItem key={release.version} value={release.version}>
                {release.version}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={saveDisabled}
          onClick={() =>
            onSave({
              latestVersion: latestVersion || null,
              minimumVersion: minimumVersion || null,
            })
          }
        >
          Save policy
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}>
          Refresh releases
        </Button>
      </div>

      {!hasReleases && (
        <Typography className={classes.warning}>
          No S3 releases found yet. Publish an `ouro-guard-v*` release before selecting a version.
        </Typography>
      )}
      {envOverrideActive && (
        <Typography className={classes.warning}>
          Environment override active
          {envOverrides.latestVersion ? `: latest=${envOverrides.latestVersion}` : ""}
          {envOverrides.minimumVersion ? ` minimum=${envOverrides.minimumVersion}` : ""}. Edge will
          use the override until it is removed.
        </Typography>
      )}
    </Paper>
  );
}
